import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import TiptapEditor from "../common/TiptapEditor";
import { X, Upload, Image as ImageIcon, BookOpen } from "lucide-react";
import { publicCourseService } from "../../services/publicCourseService";
import { uploadService } from "../../services/uploadService";
import {
  PublicCourse,
  CreatePublicCourseRequest,
  UpdatePublicCourseRequest,
} from "../../types/landing";

interface CourseFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  course?: PublicCourse | null; // If provided, we're editing
}

const CourseFormModal: React.FC<CourseFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  course,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [description, setDescription] = useState("");
  const [curriculumText, setCurriculumText] = useState("");
  const [requirementsText, setRequirementsText] = useState("");
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreatePublicCourseRequest>();

  const isEditing = !!course;

  useEffect(() => {
    if (isOpen) {
      if (isEditing && course) {
        // Populate form with existing course data
        setValue("title", course.title);
        setValue("category", course.category);
        setValue("duration", course.duration);
        setValue("status", course.status);
        setValue("displayOrder", course.displayOrder);
        setValue("curriculum", course.curriculum || []);
        setValue("requirements", course.requirements || []);
        setCurriculumText((course.curriculum || []).join("\n"));
        setRequirementsText((course.requirements || []).join("\n"));
        setValue("pricing.amount", course.pricing?.amount || 0);
        setValue("pricing.currency", course.pricing?.currency || "KES");
        setValue("pricing.isFree", course.pricing?.isFree || false);
        setValue("imageUrl", course.imageUrl || "");
        setDescription(course.description);
      } else {
        // Reset form for new course
        reset();
        setDescription("");
        setCurriculumText("");
        setRequirementsText("");
        setSelectedImageFile(null);
      }
    }
  }, [isOpen, isEditing, course, setValue, reset]);

  const handleImageUpload = async (file: File) => {
    // Store the file locally instead of uploading immediately
    setSelectedImageFile(file);
    // Create a preview URL for display
    const previewUrl = URL.createObjectURL(file);
    setValue("imageUrl", previewUrl);
    toast.success(
      "Image selected! It will be uploaded when you save the course."
    );
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit for course images
        toast.error("Image size should be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      handleImageUpload(file);
    }
  };

  const onSubmit = async (data: CreatePublicCourseRequest) => {
    setLoading(true);
    try {
      // Start with existing image URL (for editing) or form data
      let imageUrl = isEditing && course ? course.imageUrl : data.imageUrl;

      // Upload image only if a new file is selected
      if (selectedImageFile) {
        setUploadingImage(true);
        try {
          const uploadResult = await uploadService.uploadFile(
            selectedImageFile,
            "landingpage/courses"
          );
          if (uploadResult.success && uploadResult.data) {
            imageUrl = uploadResult.data.url;
          } else {
            throw new Error("Failed to upload image");
          }
        } finally {
          setUploadingImage(false);
        }
      }

      const courseData = {
        ...data,
        imageUrl,
        description,
        pricing: {
          ...data.pricing,
          isFree: String(data.pricing?.isFree) === "true",
        },
      };

      if (isEditing && course) {
        await publicCourseService.updateCourse(
          course._id,
          courseData as UpdatePublicCourseRequest
        );
        toast.success("Course updated successfully!");
      } else {
        await publicCourseService.createCourse(courseData);
        toast.success("Course created successfully!");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save course. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Center the modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 z-10 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-blue-600" />
              {isEditing ? "Edit Course" : "Create Course"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Title *
              </label>
              <input
                type="text"
                {...register("title", { required: "Title is required" })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.title ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter course title"
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* Category and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  {...register("category", {
                    required: "Category is required",
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.category ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select category</option>
                  <option value="technology">Technology</option>
                  <option value="language">Language</option>
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.category.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  {...register("status")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Duration and Display Order */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration
                </label>
                <input
                  type="text"
                  {...register("duration")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 3 months, 6 weeks, 40 hours"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  {...register("displayOrder", {
                    min: { value: 0, message: "Must be 0 or greater" },
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                  min="0"
                />
                {errors.displayOrder && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.displayOrder.message}
                  </p>
                )}
                <p className="text-gray-500 text-sm mt-1">
                  Lower numbers appear first
                </p>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Pricing
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Is Free Course
                  </label>
                  <select
                    {...register("pricing.isFree")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="false">Paid</option>
                    <option value="true">Free</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Price Amount
                  </label>
                  <input
                    type="number"
                    {...register("pricing.amount", {
                      min: { value: 0, message: "Must be 0 or greater" },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  {errors.pricing?.amount && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.pricing.amount.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Currency
                  </label>
                  <select
                    {...register("pricing.currency")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="KES">KES (KSh)</option>
                  </select>
                </div>
              </div>
              <p className="text-gray-500 text-sm">
                If course is free, price amount will be ignored
              </p>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Image
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="course-image-upload"
                  disabled={uploadingImage}
                />
                <label
                  htmlFor="course-image-upload"
                  className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 ${
                    uploadingImage ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <Upload className="h-4 w-4" />
                  {uploadingImage ? "Uploading..." : "Choose Image"}
                </label>
                {watch("imageUrl") && (
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <img
                        src={watch("imageUrl")}
                        alt="Course preview"
                        className="w-16 h-16 object-cover rounded-md border border-gray-300"
                        onError={(e) => {
                          // Hide broken images
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                        <ImageIcon className="h-3 w-3" />
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {selectedImageFile
                        ? "New image selected"
                        : "Current image"}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Description *
              </label>
              <TiptapEditor
                value={description}
                onChange={setDescription}
                placeholder="Describe the course content, objectives, and what students will learn..."
              />
            </div>

            {/* Curriculum */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Curriculum (one topic per line)
              </label>
              <textarea
                value={curriculumText}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Introduction to the subject&#10;Basic concepts and principles&#10;Practical applications&#10;Advanced topics&#10;Final project"
                onChange={(e) => {
                  const text = e.target.value;
                  setCurriculumText(text);
                  const curriculum = text
                    .split("\n")
                    .map((c) => c.trim())
                    .filter((c) => c);
                  setValue("curriculum", curriculum);
                }}
              />
            </div>

            {/* Requirements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Requirements (one requirement per line)
              </label>
              <textarea
                value={requirementsText}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Basic computer skills&#10;High school diploma&#10;English proficiency&#10;Previous experience in related field"
                onChange={(e) => {
                  const text = e.target.value;
                  setRequirementsText(text);
                  const requirements = text
                    .split("\n")
                    .map((r) => r.trim())
                    .filter((r) => r);
                  setValue("requirements", requirements);
                }}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading
                  ? "Saving..."
                  : isEditing
                  ? "Update Course"
                  : "Create Course"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CourseFormModal;
